import { z } from 'zod';

const nameSchema = z.string().trim().min(1, 'Name is required').max(255, 'Name too long');

const descriptionSchema = z
  .union([z.string(), z.null(), z.literal('')])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null || value === '') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  });

const priceCentsSchema = z
  .number()
  .int('Price must be a whole number of cents')
  .min(1, 'Price must be at least 0.01');

const currencySchema = z
  .string()
  .length(3, 'Currency must be 3 characters')
  .transform((value) => value.toUpperCase());

const billingIntervalSchema = z.enum(['month', 'year'], {
  errorMap: () => ({ message: 'Billing interval must be month or year' }),
});

export const courtHireDiscountPercentSchema = z
  .number({ invalid_type_error: 'Enter a valid discount' })
  .min(0, 'Discount cannot be negative')
  .max(100, 'Discount cannot exceed 100%');

const isActiveSchema = z.boolean();

export const membershipTermTypeSchema = z.enum(['recurring', 'fixed_period']);
export const membershipPeriodAnchorSchema = z.enum(['season', 'join_date']);

const nullableProofLabelSchema = z
  .union([z.string(), z.null(), z.literal('')])
  .optional()
  .transform((value) => {
    if (value === undefined || value === null || value === '') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
  });

/** Fields shared by create/update that describe term shape, seats, eligibility, and visibility. */
const membershipTypeExtendedFields = {
  termType: membershipTermTypeSchema.default('recurring'),
  periodAnchor: membershipPeriodAnchorSchema.nullable().optional(),
  periodMonths: z.number().int().positive('Period must be a positive number of months').nullable().optional(),
  // 0 is valid for standalone junior-only plans (e.g. a "Junior" membership with 1 junior seat, 0 adult seats)
  maxAdults: z.number().int().min(0, 'Adult seats cannot be negative').default(1),
  includedJuniors: z.number().int().min(0, 'Included juniors cannot be negative').default(0),
  extraJuniorsFree: z.boolean().default(false),
  maxTotalSeats: z.number().int().min(1, 'Max total seats must be at least 1').nullable().optional(),
  maxAgeAtJoin: z.number().int().min(0, 'Max age cannot be negative').nullable().optional(),
  minAgeAtJoin: z.number().int().min(0, 'Min age cannot be negative').nullable().optional(),
  requiresProof: z.boolean().default(false),
  proofLabel: nullableProofLabelSchema,
  requiresLinkedJunior: z.boolean().default(false),
  isPlaying: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
  isPublic: z.boolean().default(false),
};

/** Cross-field term-shape and seat-composition validation shared by create and update (update only checks fields that were supplied). */
function refineTermShape<
  T extends {
    termType?: string;
    periodAnchor?: string | null;
    periodMonths?: number | null;
    maxAdults?: number;
    includedJuniors?: number;
    maxTotalSeats?: number | null;
  },
>(data: T, ctx: z.RefinementCtx): void {
  if (data.termType === 'fixed_period' && !data.periodAnchor) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A fixed-period plan requires a period anchor (season or join_date)',
      path: ['periodAnchor'],
    });
  }
  if (data.periodAnchor === 'join_date' && !data.periodMonths) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Join-date anchored plans require a number of months',
      path: ['periodMonths'],
    });
  }
  if (data.maxAdults !== undefined && data.includedJuniors !== undefined && data.maxAdults + data.includedJuniors < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'A plan must cover at least 1 adult or junior seat',
      path: ['maxAdults'],
    });
  }
  if (
    data.maxTotalSeats != null &&
    data.maxAdults !== undefined &&
    data.includedJuniors !== undefined &&
    data.maxTotalSeats < data.maxAdults + data.includedJuniors
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Max total seats cannot be less than the covered adult and junior seats',
      path: ['maxTotalSeats'],
    });
  }
}

export const membershipTypeCreateSchema = z
  .object({
    name: nameSchema,
    description: descriptionSchema,
    priceCents: priceCentsSchema,
    currency: currencySchema,
    billingInterval: billingIntervalSchema,
    courtHireDiscountPercent: courtHireDiscountPercentSchema,
    isActive: isActiveSchema.default(true),
    ...membershipTypeExtendedFields,
  })
  .superRefine(refineTermShape);

export const membershipTypeUpdateSchema = z
  .object({
    name: nameSchema.optional(),
    description: descriptionSchema,
    priceCents: priceCentsSchema.optional(),
    currency: currencySchema.optional(),
    billingInterval: billingIntervalSchema.optional(),
    courtHireDiscountPercent: courtHireDiscountPercentSchema.optional(),
    isActive: isActiveSchema.optional(),
    termType: membershipTermTypeSchema.optional(),
    periodAnchor: membershipPeriodAnchorSchema.nullable().optional(),
    periodMonths: z.number().int().positive().nullable().optional(),
    maxAdults: z.number().int().min(0).optional(),
    includedJuniors: z.number().int().min(0).optional(),
    extraJuniorsFree: z.boolean().optional(),
    maxTotalSeats: z.number().int().min(1).nullable().optional(),
    maxAgeAtJoin: z.number().int().min(0).nullable().optional(),
    minAgeAtJoin: z.number().int().min(0).nullable().optional(),
    requiresProof: z.boolean().optional(),
    proofLabel: nullableProofLabelSchema,
    requiresLinkedJunior: z.boolean().optional(),
    isPlaying: z.boolean().optional(),
    sortOrder: z.number().int().optional(),
    isPublic: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.termType !== undefined || data.periodAnchor !== undefined || data.periodMonths !== undefined) {
      refineTermShape(data, ctx);
    }
  });

export const membershipTypeDiscountUpdateSchema = z.object({
  courtHireDiscountPercent: courtHireDiscountPercentSchema,
});

export const membershipTypeDrawerFormSchema = z
  .object({
    name: nameSchema,
    description: z.string().optional(),
    price: z.coerce.number().min(0.01, 'Price must be at least 0.01'),
    billingInterval: billingIntervalSchema,
    courtHireDiscountPercent: courtHireDiscountPercentSchema,
    isActive: isActiveSchema,
    termType: membershipTermTypeSchema.default('recurring'),
    periodAnchor: membershipPeriodAnchorSchema.nullable().optional(),
    periodMonths: z
      .number()
      .int()
      .min(1, 'Period must be a positive number of months')
      .nullable()
      .optional(),
    maxAdults: z.coerce.number().int().min(0).default(1),
    includedJuniors: z.coerce.number().int().min(0).default(0),
    extraJuniorsFree: z.boolean().default(false),
    maxTotalSeats: z
      .number()
      .int()
      .min(1, 'Max total seats must be at least 1')
      .nullable()
      .optional(),
    maxAgeAtJoin: z.number().int().min(0, 'Max age cannot be negative').nullable().optional(),
    minAgeAtJoin: z.number().int().min(0, 'Min age cannot be negative').nullable().optional(),
    requiresProof: z.boolean().default(false),
    proofLabel: z.string().optional(),
    requiresLinkedJunior: z.boolean().default(false),
    isPlaying: z.boolean().default(true),
    isPublic: z.boolean().default(false),
  })
  .superRefine(refineTermShape);

export type MembershipTypeCreateInput = z.infer<typeof membershipTypeCreateSchema>;
export type MembershipTypeUpdateInput = z.infer<typeof membershipTypeUpdateSchema>;
export type MembershipTypeDrawerFormValues = z.infer<typeof membershipTypeDrawerFormSchema>;

function drawerCommonPayloadFields(values: MembershipTypeDrawerFormValues) {
  return {
    termType: values.termType,
    periodAnchor: values.periodAnchor ?? null,
    periodMonths: values.periodMonths ?? null,
    maxAdults: values.maxAdults,
    includedJuniors: values.includedJuniors,
    extraJuniorsFree: values.extraJuniorsFree,
    maxTotalSeats: values.maxTotalSeats ?? null,
    maxAgeAtJoin: values.maxAgeAtJoin ?? null,
    minAgeAtJoin: values.minAgeAtJoin ?? null,
    requiresProof: values.requiresProof,
    proofLabel: values.proofLabel ?? null,
    requiresLinkedJunior: values.requiresLinkedJunior,
    isPlaying: values.isPlaying,
    isPublic: values.isPublic,
  };
}

export function membershipTypeFormToCreatePayload(
  values: MembershipTypeDrawerFormValues,
  currency: string
): MembershipTypeCreateInput {
  return membershipTypeCreateSchema.parse({
    name: values.name,
    description: values.description,
    priceCents: Math.round(values.price * 100),
    currency,
    billingInterval: values.billingInterval,
    courtHireDiscountPercent: values.courtHireDiscountPercent,
    isActive: values.isActive,
    ...drawerCommonPayloadFields(values),
  });
}

export function membershipTypeFormToUpdatePayload(
  values: MembershipTypeDrawerFormValues,
  currency: string
): MembershipTypeUpdateInput {
  return membershipTypeUpdateSchema.parse({
    name: values.name,
    description: values.description,
    priceCents: Math.round(values.price * 100),
    currency,
    billingInterval: values.billingInterval,
    courtHireDiscountPercent: values.courtHireDiscountPercent,
    isActive: values.isActive,
    ...drawerCommonPayloadFields(values),
  });
}
